import {
  isSalesAccountConnected,
  isAccountingAccountConnected,
} from '@/utils/external-accounts';
import {
  isCountryWithPostalCode,
  isQualifiedNorthAmericanCountry,
} from '@/data/country-code-with-names';
import { ONBOARDING_2_ROUTES } from '@/data/onboarding';
import { ProductSegmentLabels } from '@clearbanc/data-common-types';
import { BANK_ERROR_STATUSES } from '@/data/bank-connection';
import { LocalStorage } from '@/utils/local-storage';
import { UserResponseDto } from '@clearbanc/baus-sdk';
import { BusinessDetails } from '@/types/business.types';
import { Advance } from '@/types/advance.types';
import { ExternalAccount } from '@/types/external-account.types';
import { BankAccount } from '@/types/banking.types';
import { FileUpload } from '@/types/file-upload.types';
import { areDocumentUploadsRequired } from '@/utils/document-upload-requirements';
/**
 * The following helper functions assist in confirming whether specific groups of data have been
 * gathered from the user in order to determine at current stage of the onboarding flow. and may impact
 * routing, component rendering among others. These functions are used by onboarding-2 flow
 *
 * NOTE: when editing existing functions, ensure to test all components/modules that utilize the functions
 * and confirm with each respective pod that the changes are expected and approrpiate.
 */

export function isBasicUserDetailsStepComplete(user: UserResponseDto): boolean {
  return !!(user.firstName && user.lastName && user.phoneNumber);
}

export function isBasicBusinessDetailsStepComplete(
  business: BusinessDetails,
): boolean {
  return !!(
    business.name &&
    business.website &&
    business.vertical &&
    business.qualifyingQuestions?.monthly_revenue
  );
}

// OFFERS
export function isOffersStepComplete(
  advanceInNegotiation: Advance,
  business: BusinessDetails,
): boolean {
  if (business.productSegment.label === ProductSegmentLabels.BNPL) {
    return true;
  }
  const { offerOptions } = advanceInNegotiation || {
    offerOptions: [],
  };

  if (!offerOptions || !offerOptions.length) {
    return false;
  }

  return offerOptions.some((offer) => !!offer.selectedAt);
}

// CONNECT ACCOUNTS
export function isConnectSalesAccountsStepComplete(
  externalAccounts: ExternalAccount[],
): boolean {
  return isSalesAccountConnected(externalAccounts);
}

// BANKING

export function isPlaidConnected(rootGetters: any): boolean {
  return (
    (rootGetters.bankAccounts.length > 0 &&
      !rootGetters.isPrimaryBankAccountDisconnected) ||
    rootGetters.primaryBankAccountAuthorizedGoCardless
  );
}

export function isConnectBankingStepComplete(rootGetters: any): boolean {
  return isPlaidConnected(rootGetters) || rootGetters.hasUploadedBankDocs;
}

export function isBankLinkConfirmationStepComplete(rootGetters: any): boolean {
  return (
    (rootGetters.bankAccounts.some((bankAccount: BankAccount) => {
      if (!bankAccount.details?.setAsPrimary) {
        return false;
      }

      const statusSummary = bankAccount?.connection?.plaidStatus?.summary;
      const hasValidStatus =
        !statusSummary || !BANK_ERROR_STATUSES.includes(statusSummary);

      return hasValidStatus || rootGetters.businessOnPlaidBypass;
    }) &&
      !rootGetters.isReorderBankingStepsEnabled) ||
    (rootGetters.primaryBankAccount &&
      (!rootGetters.isPrimaryBankAccountDisconnected ||
        rootGetters.businessOnPlaidBypass))
  );
}

export function isUploadBankStatementsStepComplete(rootGetters: any): boolean {
  const bankAccount =
    rootGetters.primaryBankAccount ?? rootGetters.currentBankAccountToBePrimary;
  if (!bankAccount) return false;
  if (rootGetters.isHeronDiligenceChecksEnabled) {
    return rootGetters.pdfStatementsCompletionDetails.isComplete;
  }
  return true;
}

// BUSINESS DETAILS
function getRequiredAddressFields(business: BusinessDetails): string[] {
  if (business.shippingAddressSame) return [];

  const addressFields = [
    'addressStreet1',
    'addressCity',
    'addressState',
    'addressCountryCode',
  ];

  const shippingFields = [
    'shippingAddressStreet1',
    'shippingAddressCity',
    'shippingAddressState',
    'shippingAddressCountryCode',
  ];

  if (isCountryWithPostalCode(business.shippingAddressCountryCode))
    shippingFields.push('shippingAddressPostalCode');

  if (isCountryWithPostalCode(business.addressCountryCode))
    addressFields.push('addressPostalCode');

  return business.shippingAddressSame
    ? addressFields
    : addressFields.concat(shippingFields);
}

export function isBusinessDetailsStepComplete(
  business: BusinessDetails,
): boolean {
  const requiredFields = ['name', 'phone'];

  const addressFields = getRequiredAddressFields(business);

  const allRequiredFields = requiredFields.concat(addressFields);

  // @ts-ignore
  return allRequiredFields.every((field) => !!business[field]);
}

export function isBizProfileBusinessDetailsStepComplete(
  business: BusinessDetails,
  rootGetters: any,
): boolean {
  const requiredFields = ['name', 'phone', 'federalTaxId', 'corpDate'];
  const requiredUploads = [];

  // Require incorporation docs upload
  requiredUploads.push(rootGetters.incorporationUploadMatcher);

  const requiredAddressFields = getRequiredAddressFields(business);

  const allRequiredFields = requiredFields.concat(requiredAddressFields);

  const requiredFieldsComplete = allRequiredFields.every(
    // @ts-ignore
    (field) => !!business[field],
  );

  const requiredUploadsComplete = requiredUploads.length
    ? requiredUploads.every((uploadParams) => {
        return !!rootGetters.uploadsMatching({
          ...uploadParams,
          ...(rootGetters.isDiligenceServiceReadDataEnabled
            ? { externalId: business.id }
            : { businessId: business.id }),
        }).length;
      })
    : true;
  if (
    !areDocumentUploadsRequired({
      isLexisNexisDDPEnabled: rootGetters.isLexisNexisDDPEnabled,
      businessVerification: rootGetters.businessVerification,
      businessUploads: rootGetters.uploads,
    })
  ) {
    return requiredFieldsComplete;
  }

  return requiredFieldsComplete && requiredUploadsComplete;
}

export function isBusinessDetailsComplete(business: BusinessDetails): boolean {
  return !!(
    business.corpCountry &&
    business.corpState &&
    business.corpType &&
    business.name
  );
}

export function isRegisteredAddressStepComplete(
  business: BusinessDetails,
): boolean {
  const shippingFields = [
    'shippingAddressStreet1',
    'shippingAddressCity',
    'shippingAddressState',
    'shippingAddressCountryCode',
  ];
  const requiredFields = [
    'phone',
    'addressStreet1',
    'addressCity',
    'addressState',
    'addressCountryCode',
  ];
  if (isCountryWithPostalCode(business.shippingAddressCountryCode))
    shippingFields.push('shippingAddressPostalCode');
  if (isCountryWithPostalCode(business.addressCountryCode))
    requiredFields.push('addressPostalCode');
  const allRequiredFields = requiredFields.concat(
    business.shippingAddressSame ? [] : shippingFields,
  );
  // @ts-ignore
  return allRequiredFields.every((field) => !!business[field]);
}

export function isBusinessFormationDocsStepComplete(rootGetters: any): boolean {
  return !!(
    rootGetters.business.registrationNumber &&
    rootGetters.business.corpDate &&
    rootGetters.uploadsMatching(rootGetters.einUploadMatcher)
  );
}

// OWNERS

export function isOwnersStepComplete(
  owners: UserResponseDto[],
  isOwnerInfoComplete: any,
): boolean {
  return owners.some((owner) => isOwnerInfoComplete(owner.id));
}

export function isOwnersListStepComplete(
  user?: UserResponseDto,
  authorizedSignatory?: UserResponseDto,
): boolean {
  return !!authorizedSignatory && !!user?.agreedToKyc;
}

export function hasOwnerVerificationDocsBeenUploaded(
  uploads: FileUpload[],
  ownerId: string | number,
): FileUpload | undefined {
  return uploads.find((file) => file.metaId === ownerId.toString());
}

export function findIncompleteOwnerStepOne(
  allOwners: UserResponseDto[],
  uploads: FileUpload[],
): UserResponseDto | undefined {
  return allOwners.find(
    (owner) =>
      !owner.firstName ||
      !owner.lastName ||
      !hasOwnerVerificationDocsBeenUploaded(uploads, owner.id),
  );
}

export function isOwnersStepOneComplete(
  allOwners: UserResponseDto[],
  uploads: FileUpload[],
): boolean {
  return !!(
    allOwners.length && !findIncompleteOwnerStepOne(allOwners, uploads)
  );
}

export function findIncompleteOwner(
  allOwners: UserResponseDto[],
): UserResponseDto | undefined {
  return allOwners.find((owner) => {
    return !owner.profileCompleted;
  });
}

export function isOwnersComplete(getters: any): boolean {
  return (
    !findIncompleteOwner(getters.allBusinessProfiles) && !!getters.hasSignatory
  );
}

// CONTRACT

export function isBankContractStepComplete(rootGetters: any): boolean {
  return (
    !isQualifiedNorthAmericanCountry(rootGetters.business.corpCountry) ||
    rootGetters.isPrimaryBankAccountContractSigned ||
    (rootGetters.hasUploadedBankDocs &&
      (rootGetters.isBankAccountToBePrimaryObfuscatingData ||
        !isPlaidConnected(rootGetters)))
  );
}

export function isAdvanceContractStepComplete(rootGetters: any): boolean {
  return (
    rootGetters.isAdvanceContractSigned ||
    (!rootGetters.isAuthorizedSignatory &&
      isBankContractStepComplete(rootGetters))
  );
}

// STEP SUMMARY

export function isMissedStepsStepComplete(missedSteps?: any): boolean {
  return !(missedSteps?.length > 0);
}

export function isStepSkippedInLocalStorage(stepName: string): boolean {
  return !!LocalStorage.getItem(LocalStorage.getSkippedStepName(stepName));
}

export function wasMarketingStepSkipped(getters: any): boolean {
  return (
    isStepSkippedInLocalStorage(ONBOARDING_2_ROUTES.CONNECT_MARKETING) ||
    isAccountingAccountConnected(getters.externalAccounts) ||
    isPlaidConnected(getters) ||
    getters.hasUploadedBankDocs ||
    isBusinessDetailsComplete(getters.business)
  );
}

export function wasAccountingStepSkipped(getters: any): boolean {
  return (
    isStepSkippedInLocalStorage(ONBOARDING_2_ROUTES.CONNECT_ACCOUNTING) ||
    isPlaidConnected(getters) ||
    getters.hasUploadedBankDocs ||
    isBusinessDetailsComplete(getters.business)
  );
}

export function wasPlaidConnectStepSkipped(getters: any): boolean {
  return (
    isStepSkippedInLocalStorage(ONBOARDING_2_ROUTES.CONNECT_BANKING) ||
    getters.hasUploadedBankDocs ||
    isBusinessDetailsComplete(getters.business)
  );
}

export function wasUploadBankStatementsStepSkipped(getters: any): boolean {
  return (
    isStepSkippedInLocalStorage(ONBOARDING_2_ROUTES.BANK_FAILED_UPLOAD_DOCS) ||
    isBusinessDetailsComplete(getters.business)
  );
}
